import type {
  DataProvider,
  DatastoreNameValueParam,
  DatastoreProvider,
  DatastoreQueryJson,
  FormPayload,
  FormStructure,
} from '../types/types';

import { normalizeRuleValuesJson } from '../../features/DatastoreDataProvider/utils/queryLimitUtils';

import { isDatastoreProvider } from './typeGuards';

/** API wire shape for DATASTORE provider (`datastore` holds mapping + request fields). */
export interface DatastoreWirePayload {
  type: 'DATASTORE';
  name?: string;
  description?: string;
  dependencies?: Record<string, string[]>;
  datastore: {
    mapping: Array<{ value: string; label: string }>;
    templateId: string;
    distinct: boolean;
    sortBy?: string;
    sortOrder?: string;
    pageSize?: number;
    baseParams: DatastoreNameValueParam[];
    fields: string[];
    rqbFilters: DatastoreQueryJson;
  };
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }

  return null;
}

/**
 * Flatten wire-format Datastore provider (from API) into in-app {@link DatastoreProvider}.
 */
export function deserializeDatastoreProviderFromWire(
  raw: Record<string, unknown>
): DatastoreProvider {
  // Legacy fallback: backend previously used `savOq` / `savOqRequest` keys before the rename to `datastore`.
  const nested = asRecord(raw.datastore ?? raw.savOq);
  const mapping = nested && Array.isArray(nested.mapping) ? nested.mapping : [];
  const first = asRecord(mapping[0]);
  const legacyDatastoreRequest = nested?.savOqRequest;
  const legacyReq =
    legacyDatastoreRequest !== null &&
    typeof legacyDatastoreRequest === 'object' &&
    !Array.isArray(legacyDatastoreRequest)
      ? asRecord(legacyDatastoreRequest as Record<string, unknown>)
      : null;
  const req = legacyReq ?? nested ?? {};

  const valueField = String(first?.value ?? '');
  const labelField = String(first?.label ?? '');
  const templateId = String(req.templateId ?? req.objectKey ?? '');

  const rqbRaw = req.rqbFilters ?? req.fieldExpressions;
  const query: DatastoreQueryJson =
    rqbRaw !== null && typeof rqbRaw === 'object' && !Array.isArray(rqbRaw)
      ? (rqbRaw as DatastoreQueryJson)
      : {};

  const params = Array.isArray(req.baseParams ?? req.params)
    ? ((req.baseParams ?? req.params) as DatastoreNameValueParam[])
    : [];

  return {
    type: 'DATASTORE',
    name: typeof raw.name === 'string' ? raw.name : undefined,
    description:
      typeof raw.description === 'string' ? raw.description : undefined,
    templateId,
    labelField,
    valueField,
    query,
    distinct: Boolean(req.distinct),
    sortBy: typeof req.sortBy === 'string' ? req.sortBy : undefined,
    sortOrder:
      req.sortOrder === 'asc' || req.sortOrder === 'desc' ? req.sortOrder : '',
    params,
    pageSize: (() => {
      if (typeof raw.pageSize === 'number') {
        return raw.pageSize;
      }

      if (typeof req.pageSize === 'number') {
        return req.pageSize;
      }

      return undefined;
    })(),
    dependencies:
      raw.dependencies && typeof raw.dependencies === 'object'
        ? (raw.dependencies as Record<string, string[]>)
        : undefined,
  };
}

function isDatastoreWireShape(dp: unknown): boolean {
  const o = asRecord(dp);

  if (!o || o.type !== 'DATASTORE') {
    return false;
  }

  return (
    ('datastore' in o && asRecord(o.datastore) != null) ||
    // Legacy fallback: previously persisted as `savOq`.
    ('savOq' in o && asRecord(o.savOq) != null)
  );
}

/**
 * Normalize a single data provider after load (wire Datastore → flat {@link DatastoreProvider}).
 */
export function normalizeDataProviderFromApi(dp: unknown): DataProvider {
  if (isDatastoreWireShape(dp)) {
    return deserializeDatastoreProviderFromWire(dp as Record<string, unknown>);
  }

  return dp as DataProvider;
}

/**
 * Normalize all data providers from API `formData.dataProviders`.
 */
export function normalizeDataProvidersFromApi(
  record: Record<string, DataProvider> | undefined | null
): Record<string, DataProvider> {
  if (!record) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(record).map(([id, v]) => [
      id,
      normalizeDataProviderFromApi(v),
    ])
  );
}

export function normalizeFormPayloadDatastoreInbound(
  payload: FormPayload
): FormPayload {
  const fd = payload.formData;

  if (!fd?.dataProviders) {
    return payload;
  }

  return {
    ...payload,
    formData: {
      ...fd,
      dataProviders: normalizeDataProvidersFromApi(fd.dataProviders),
    },
  };
}

/**
 * Serialize flat {@link DatastoreProvider} to API wire shape (nested `datastore`).
 */
export function serializeDatastoreProviderForApi(
  dp: DatastoreProvider
): DatastoreWirePayload {
  const params = (dp.params ?? []).filter(
    (p) =>
      String(p.name ?? '').trim() !== '' || String(p.value ?? '').trim() !== ''
  );

  const fields = [dp.valueField, dp.labelField].filter(
    (f) => String(f ?? '').trim() !== ''
  );

  // Safety net: normalize rule values for operator-specific formats (unary → null, in/notIn → array).
  const rqbFilters: DatastoreQueryJson =
    dp.query && typeof dp.query === 'object'
      ? normalizeRuleValuesJson(dp.query)
      : {};

  const mapping: Array<{ value: string; label: string }> = [];

  if (dp.valueField || dp.labelField) {
    mapping.push({
      value: dp.valueField ?? '',
      label: dp.labelField ?? '',
    });
  }

  const base: DatastoreWirePayload = {
    type: 'DATASTORE',
    name: dp.name,
    datastore: {
      mapping,
      templateId: dp.templateId,
      distinct: dp.distinct ?? false,
      /*
       * sortOrder: The sort direction for results (asc/desc).
       * sortBy: The column to sort on — defaults to valueField (the display column).
       * Both are only included when sortOrder is set (truthy — excludes empty/None).
       */
      ...(dp.sortOrder
        ? {
            sortOrder: dp.sortOrder,
            ...(dp.sortBy || dp.valueField
              ? { sortBy: dp.sortBy || dp.valueField }
              : {}),
          }
        : {}),
      ...(dp.pageSize ? { pageSize: dp.pageSize } : {}),
      baseParams: params,
      fields,
      rqbFilters,
    },
  };

  if (dp.description) {
    base.description = dp.description;
  }

  if (dp.dependencies && Object.keys(dp.dependencies).length > 0) {
    base.dependencies = dp.dependencies;
  }

  return base;
}

/**
 * Replace each Datastore entry with wire format; leave other provider types unchanged.
 */
export function serializeDataProvidersForApi(
  record: Record<string, DataProvider>
): Record<string, DataProvider | DatastoreWirePayload> {
  return Object.fromEntries(
    Object.entries(record).map(([k, v]) => [
      k,
      isDatastoreProvider(v) ? serializeDatastoreProviderForApi(v) : v,
    ])
  );
}

export function withWireDatastoreFormPayload(
  formPayload: FormPayload
): FormPayload {
  const fd = formPayload.formData;

  if (!fd?.dataProviders) {
    return formPayload;
  }

  return {
    ...formPayload,
    formData: {
      ...fd,
      /* Wire Datastore shape is not assignable to in-app DataProvider; JSON body matches backend contract. */
      dataProviders: serializeDataProvidersForApi(
        fd.dataProviders
      ) as unknown as FormStructure['dataProviders'],
    },
  };
}
